import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HeaderComponent } from '../../../common/header/header.component';
import {AsyncPipe, DecimalPipe, NgClass, NgFor, NgIf} from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { CompanyDetailsModalComponent } from '../company-details-modal/company-details-modal.component';
import { ExportModalComponent } from '../export-modal/export-modal.component';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription, combineLatest, map, take, filter, skip } from "rxjs";
import { selectLoading } from "../../../state/search/search.selectors";
import {
  selectCompanies,
  selectFilterTypes,
  selectSearchFilters,
  selectSearchPayload,
  selectTotalRecords,
  selectNextPageToken,
  selectPaginationCount,
  selectCurrentPage,
  selectPageSize,
  selectPreviousPageToken,
  selectAdvancedSearchSuccess,
  selectAdvancedSearchFailure
} from "../../../state/advancedSearch/advanced-search.selectors";
import {
  searchPeople,
  searchCompanies,
  clearSearchResults,
  clearPeople
} from '../../../state/advancedSearch/advanced-search.actions';
import { PopupService } from "../../../services/popup.service";
import { AdvancedSearchService } from "../../../services/advanced-search.service";
import {MatTooltip} from "@angular/material/tooltip";
import { ProceedToLeadsModalComponent } from '../proceed-to-leads-modal/proceed-to-leads-modal.component';
import { ProspectingListSummary, ProspectingService } from '../../../services/prospecting.service';

@Component({
  selector: 'app-companies',
  standalone: true,
  imports: [HeaderComponent, NgFor, RouterModule, NgIf, AsyncPipe, NgClass, DecimalPipe, CompanyDetailsModalComponent, ExportModalComponent, MatTooltip, ProceedToLeadsModalComponent],
  templateUrl: './companies.component.html',
  styleUrl: './companies.component.scss',
})
export class CompaniesComponent implements OnInit, OnDestroy, AfterViewChecked {
  private static readonly SCROLL_KEY = 'companiesListScrollTop';
  private static readonly PEOPLE_COUNTS_KEY = 'companiesPeopleCounts';

  @ViewChild('companiesList') private companiesList?: ElementRef<HTMLElement>;
  private scrollRestored = false;

  // Data observables
  companies$: Observable<any>;
  loading$: Observable<boolean>;
  totalRecords$: Observable<number>;
  searchFilters$: Observable<any>;
  searchPayload$: Observable<any>;
  filterTypes$: Observable<any[]>;

  // Pagination observables
  nextPageToken$: Observable<string | null>;
  currentPage$: Observable<number>;
  pageSize$: Observable<number>;
  paginationCount$: Observable<number>;
  totalPages$: Observable<number>;
  hasNextPage$: Observable<boolean>;
  previousPageToken$: Observable<string | null>;

  // Component state
  companyLocation: string = 'Using company location';

  // Add local loading flag
  isInitialLoading: boolean = true;

  // Modal state
  isModalVisible: boolean = false;
  selectedCompany: any = null;

  // Export modal state
  isExportModalVisible: boolean = false;
  currentSearchFilters: any[] = [];

  // "Proceed to Leads" dialog state
  isProceedModalVisible: boolean = false;
  proceedFilters: any[] = [];
  proceedSummaryLabel: string = '';

  // "My prospecting lists" menu
  prospectingLists: ProspectingListSummary[] = [];
  isProspectingListsOpen: boolean = false;

  // Location data storage
  countries: any[] = [];
  regions: {[countryId: number]: any[]} = {};
  cities: {[regionId: number]: any[]} = {};

  // Per-company people-count preview state, keyed by veridion_id
  peopleCounts: { [veridionId: string]: { status: 'loading' | 'loaded' | 'error'; count?: number } } = {};

  // Enough skeleton rows to overfill the tallest realistic viewport; the
  // full-height skeleton list clips the excess (overflow-hidden).
  protected readonly skeletonRows = Array.from({ length: 15 }, (_, i) => i);

  private subscription: Subscription = new Subscription();

  private pageTokens: Map<number, string> = new Map(); // Store tokens for each page
  private currentSearchParams: any = null; // Store the last search parameters

  constructor(
    private store: Store,
    private popupService: PopupService,
    private advancedSearchService: AdvancedSearchService,
    private prospectingService: ProspectingService,
    private router: Router
  ) {
    // Initialize data observables
    this.companies$ = this.store.select(selectCompanies);
    this.loading$ = this.store.select(selectLoading);
    this.totalRecords$ = this.store.select(selectTotalRecords);
    this.searchFilters$ = this.store.select(selectSearchFilters);
    this.searchPayload$ = this.store.select(selectSearchPayload);
    this.filterTypes$ = this.store.select(selectFilterTypes);

    // Initialize pagination observables
    this.nextPageToken$ = this.store.select(selectNextPageToken);
    this.currentPage$ = this.store.select(selectCurrentPage);
    this.pageSize$ = this.store.select(selectPageSize);
    this.paginationCount$ = this.store.select(selectPaginationCount);

    // Calculate total pages
    this.totalPages$ = this.paginationCount$.pipe(
      map(count => Math.ceil(count / 10)) // Use 10 as default page size
    );

    // Add this line for previous page token
    this.previousPageToken$ = this.store.select(selectPreviousPageToken);

    // Determine if there's a next page
    this.hasNextPage$ = this.nextPageToken$.pipe(
      map(token => !!token)
    );
  }

  ngOnInit(): void {
    // Bring back people counts fetched before navigating to a people page
    this.restorePeopleCounts();

    // Prospecting lists the user already opened ("My prospecting lists" menu)
    this.loadProspectingLists();

    // Watch for when companies data arrives or loading state changes
    this.subscription.add(
      combineLatest([
        this.companies$,
        this.loading$
      ]).subscribe(([companies, loading]) => {
        // Show skeleton while:
        // 1. Store is loading, OR
        // 2. Companies is null (no data yet)
        if (loading || companies === null) {
          this.isInitialLoading = true;
          return;
        }

        // Hide skeleton when:
        // 1. Not loading, AND
        // 2. Companies is an array (even if empty - that means search completed)
        if (!loading && Array.isArray(companies)) {
          this.isInitialLoading = false;
        }
      })
    );

    // Monitor companies
    this.subscription.add(
      this.companies$.subscribe(companies => {
        // console.log('companies', companies);
      })
    );

    // If the company search fails (e.g. the Veridion provider is unavailable), stop the
    // skeleton and surface a non-blocking notice instead of leaving an endless loader.
    this.subscription.add(
      this.store.select(selectAdvancedSearchFailure)
        .pipe(filter(error => !!error))
        .subscribe(() => {
          this.isInitialLoading = false;
          this.popupService.notify(
            'This functionality is currently not available. Please try again later.',
            'Advanced Search',
            'warning'
          );
        })
    );

    // Extract company location from filters
    this.subscription.add(
      this.searchFilters$.subscribe(filters => {
        if (filters && filters.filters) {
          // Find company location filter
          const locationFilter = filters.filters.find((filter: any) => filter.filterType === 10);

          if (locationFilter && locationFilter.value) {
            const locationParts = [];

            // Extract location parts
            if (locationFilter.value.countryData) {
              locationParts.push(locationFilter.value.countryData.name);
            }

            if (locationFilter.value.regionData) {
              locationParts.push(locationFilter.value.regionData.name);
            }

            if (locationFilter.value.cityData) {
              locationParts.push(locationFilter.value.cityData.name);
            }

            // Update the company location string
            if (locationParts.length > 0) {
              this.companyLocation = locationParts.join(', ');
            }
          }
        }
      })
    );

    // Clear pagination state when new search results arrive with page 1
    // skip(1) avoids the initial re-emission on component re-mount, which would
    // otherwise wipe the scroll position we need to restore after back-navigation
    this.subscription.add(
      combineLatest([this.companies$, this.currentPage$])
        .pipe(skip(1))
        .subscribe(([companies, currentPage]) => {
          if (companies && currentPage === 1) {
            // This is a new search, clear the stored tokens
            this.pageTokens.clear();
            sessionStorage.removeItem(CompaniesComponent.SCROLL_KEY);
          }
        })
    );
  }

  // Add this helper method to re-run the search
  private rerunSearch(searchFilters: any): void {
    this.isInitialLoading = true; // Set loading when rerunning search

    this.filterTypes$.pipe(take(1)).subscribe(filterTypes => {
      const companyFilters = this.prepareCompanyFilters(searchFilters, filterTypes);

      const searchParams = {
        filters: {
          and: companyFilters
        },
        page: 1,
        pageSize: 10
      };

      this.store.dispatch(searchCompanies(searchParams));
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngAfterViewChecked(): void {
    if (this.scrollRestored || !this.companiesList) return;
    const saved = sessionStorage.getItem(CompaniesComponent.SCROLL_KEY);
    if (saved === null) return;
    const top = Number(saved);
    if (!Number.isFinite(top)) return;
    this.companiesList.nativeElement.scrollTop = top;
    this.scrollRestored = true;
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    sessionStorage.setItem(CompaniesComponent.SCROLL_KEY, String(el.scrollTop));
  }

  // Pagination methods.
  //
  // Veridion pagination tokens are cursors bound to the EXACT query that issued
  // them. We therefore replay the exact page-1 payload (stored in the store)
  // verbatim, only attaching the page token — never rebuild it through a second
  // query-builder, which would drift from page 1 (filter order, and multi-location
  // equals-vs-in) and make the cursor invalid. The rebuild is kept only as a
  // fallback for the unlikely case the payload isn't in memory.
  private dispatchPagedSearch(token: string, searchPayload: any, searchFilters: any, filterTypes: any[]): void {
    const filters = searchPayload ?? { and: this.prepareCompanyFilters(searchFilters, filterTypes) };

    this.store.dispatch(searchCompanies({
      filters,
      page: 1, // satisfies the action interface; pagination is token-driven
      pageSize: 10,
      nextPageToken: token
    }));
  }

  onNextPage(): void {
    combineLatest([
      this.nextPageToken$,
      this.searchPayload$,
      this.searchFilters$,
      this.filterTypes$
    ]).pipe(take(1)).subscribe(([nextToken, searchPayload, searchFilters, filterTypes]) => {
      if (!nextToken) return;
      this.dispatchPagedSearch(nextToken, searchPayload, searchFilters, filterTypes);
    });
  }

  onPreviousPage(): void {
    combineLatest([
      this.previousPageToken$,
      this.searchPayload$,
      this.searchFilters$,
      this.filterTypes$
    ]).pipe(take(1)).subscribe(([previousToken, searchPayload, searchFilters, filterTypes]) => {
      if (!previousToken) return;
      this.dispatchPagedSearch(previousToken, searchPayload, searchFilters, filterTypes);
    });
  }

  clearPaginationState(): void {
    this.pageTokens.clear();
    this.currentSearchParams = null;
  }

  // Collect EVERY company-location filter (not just the first) and map each to
  // the Veridion value shape using the enhanced data saved at search time
  // (countryData/regionData/cityData). Country is mandatory, so entries without
  // a resolved country are dropped.
  private extractLocationsFromFilters(filters: any[]): any[] {
    if (!filters || !Array.isArray(filters)) return [];

    return filters
      .filter(filter => filter.filterType === 10 && filter.value)
      .map(filter => {
        const locationData: any = {};
        if (filter.value.countryData) locationData.country = filter.value.countryData.name;
        if (filter.value.regionData) locationData.region = filter.value.regionData.name;
        if (filter.value.cityData) locationData.city = filter.value.cityData.name;
        return locationData.country ? locationData : null;
      })
      .filter(Boolean);
  }

  // Build the company-filters array. Mirrors onSearch() so the export payload
  // (the remaining caller) matches the search the user actually ran:
  // non-location filters in form order, then ONE aggregated company_location
  // condition ('equals' for a single location, 'in' with an array for 2+),
  // pushed last.
  private prepareCompanyFilters(formValue: any, filterTypes: any[]): any[] {
    const companyFilters: any[] = [];

    if (!formValue || !formValue.filters) return companyFilters;

    // Non-location, non-person filters in form order.
    formValue.filters.forEach((filter: any) => {
      if (!filter.filterType || filter.filterType === 10) return; // location handled below

      const filterType = filterTypes.find(f => f.id === filter.filterType);
      if (!filterType) return;

      if (filterType.applied === 'person') return; // person filters are not company filters

      const formattedFilter = this.formatFilterForSearch(filter, filterType);
      if (formattedFilter) {
        companyFilters.push(formattedFilter);
      }
    });

    // Aggregate all locations into a single company_location condition.
    const locations = this.extractLocationsFromFilters(formValue.filters);
    if (locations.length === 1) {
      companyFilters.push({
        attribute: 'company_location',
        relation: 'equals',
        value: locations[0]
      });
    } else if (locations.length > 1) {
      companyFilters.push({
        attribute: 'company_location',
        relation: 'in',
        value: locations
      });
    }

    return companyFilters;
  }

  // Single source of truth for the Apollo people-search params. Used by both
  // the full people search (navigation) and the people-count preview so the
  // two requests can never drift apart — the count is only meaningful if it
  // comes from exactly the same query the user will run.
  private buildPeopleSearchParams(companyName: string, company: any, formValue: any, filterTypes: any[]): any {
    const params: any = {
      companyName: companyName,
      selectedCompany: company,
      page: 1,
      perPage: 10
    };

    // Use by-domain search when website_domain is available
    if (company.website_domain) {
      params.website_domain = company.website_domain;
    }

    // Extract person filters and the company-location names from the saved search filters
    let personTitles: string[] = [];
    let personSeniorities: string[] = [];
    // Collect names from ALL location filters (the search supports multiple).
    // A single scalar would let the last location win, scoping people search to
    // the wrong country for multi-location searches.
    const locationNames: string[] = [];

    if (formValue?.filters && Array.isArray(filterTypes)) {
      formValue.filters.forEach((filter: any) => {
        if (!filter.filterType) return;

        const filterType = filterTypes.find((type: any) => type.id === filter.filterType);
        if (!filterType) return;

        // Location filter: collect Country/Region/City names from the saved data objects
        if (filterType.id === 10 || filterType.attribute === 'company_location') {
          if (filter.value) {
            if (filter.value.countryData) locationNames.push(filter.value.countryData.name);
            if (filter.value.regionData) locationNames.push(filter.value.regionData.name);
            if (filter.value.cityData) locationNames.push(filter.value.cityData.name);
          }
        }

        // Person filters
        if (filterType.applied === 'person' || filterType.id === 11 || filterType.id === 12) {
          if (filterType.attribute === 'person_titles' && Array.isArray(filter.value)) {
            personTitles = filter.value;
          } else if (filterType.attribute === 'person_seniorities' && Array.isArray(filter.value)) {
            personSeniorities = filter.value;
          }
        }
      });
    }

    if (personTitles.length > 0) {
      params.person_titles = personTitles;
    }

    if (personSeniorities.length > 0) {
      params.person_seniorities = personSeniorities;
    }

    // organization_locations only applies to fuzzy search (no website_domain).
    // Apollo treats the array as OR, so passing every filter location keeps a
    // company in any of the searched locations reachable.
    if (!company.website_domain) {
      if (locationNames.length > 0) {
        params.organization_locations = locationNames;
      } else if (company.main_address) {
        params.organization_locations = this.getLocation(company.main_address).split(',').map(item => item.trim());
      }
    }

    return params;
  }

  // Search for people in a company
  searchPeople(companyName: string, company: any) {
    // Clear previous people data before searching
    this.store.dispatch(clearPeople());

    // Capture current scroll position so we can restore it when the user returns
    if (this.companiesList) {
      sessionStorage.setItem(
        CompaniesComponent.SCROLL_KEY,
        String(this.companiesList.nativeElement.scrollTop),
      );
    }

    // IMPORTANT: Set a flag to indicate we're navigating to search people
    // This will help the people component know to show loading
    sessionStorage.setItem('searchingPeople', 'true');

    combineLatest([this.searchFilters$, this.filterTypes$])
      .pipe(take(1))
      .subscribe(([formValue, filterTypes]) => {
        const params = this.buildPeopleSearchParams(companyName, company, formValue, filterTypes);
        this.store.dispatch(searchPeople({ params }));
      });
  }

  // People-count preview: runs the exact same Apollo query as searchPeople()
  // but only reads the pagination total — no navigation, no store dispatch
  // (dispatching would clobber the people page's state). per_page is dropped
  // to 1 since only the total matters. Results are cached per company.
  showPeopleCount(company: any): void {
    const key = company.veridion_id;
    const existing = this.peopleCounts[key];
    if (existing && (existing.status === 'loading' || existing.status === 'loaded')) {
      return;
    }

    this.peopleCounts[key] = { status: 'loading' };

    combineLatest([this.searchFilters$, this.filterTypes$])
      .pipe(take(1))
      .subscribe(([formValue, filterTypes]) => {
        const params = {
          ...this.buildPeopleSearchParams(company.company_name, company, formValue, filterTypes),
          perPage: 1
        };

        // Same routing rule as the searchPeople$ effect
        const search$ = params.website_domain
          ? this.advancedSearchService.searchPeopleByDomain(params)
          : this.advancedSearchService.searchPeople(params);

        this.subscription.add(
          search$.subscribe({
            next: (res: any) => {
              const apollo = res?.data?.apollo_result;
              const total = apollo?.pagination?.total_entries;
              if (typeof total === 'number') {
                // Real result (including a genuine 0 from a successful search).
                this.peopleCounts[key] = { status: 'loaded', count: total };
              } else if (apollo && Array.isArray(apollo.companies) && apollo.companies.length === 0) {
                // By-domain: the domain genuinely isn't in Apollo — a true zero.
                this.peopleCounts[key] = { status: 'loaded', count: 0 };
              } else {
                // null apollo_result / missing pagination = upstream Apollo
                // failure. Don't cache a false "0 people found"; let the user retry.
                this.peopleCounts[key] = { status: 'error' };
                return;
              }
              this.persistPeopleCounts();
            },
            error: () => {
              this.peopleCounts[key] = { status: 'error' };
            }
          })
        );
      });
  }

  getPeopleCount(company: any): { status: 'loading' | 'loaded' | 'error'; count?: number } | null {
    return this.peopleCounts[company.veridion_id] || null;
  }

  // Counts survive navigating to a company's people page and back: the loaded
  // entries are kept in sessionStorage together with a fingerprint of the
  // search filters, and discarded when they came from a different search
  // (person/location filters change the counts).
  private persistPeopleCounts(): void {
    this.searchFilters$.pipe(take(1)).subscribe(filters => {
      const loaded: { [veridionId: string]: { status: 'loaded'; count?: number } } = {};
      Object.keys(this.peopleCounts).forEach(key => {
        const entry = this.peopleCounts[key];
        if (entry.status === 'loaded') {
          loaded[key] = { status: 'loaded', count: entry.count };
        }
      });

      sessionStorage.setItem(CompaniesComponent.PEOPLE_COUNTS_KEY, JSON.stringify({
        fingerprint: JSON.stringify(filters ?? null),
        counts: loaded
      }));
    });
  }

  private restorePeopleCounts(): void {
    const raw = sessionStorage.getItem(CompaniesComponent.PEOPLE_COUNTS_KEY);
    if (!raw) return;

    this.searchFilters$.pipe(take(1)).subscribe(filters => {
      try {
        const saved = JSON.parse(raw);
        if (saved?.fingerprint === JSON.stringify(filters ?? null)) {
          this.peopleCounts = saved.counts || {};
        } else {
          sessionStorage.removeItem(CompaniesComponent.PEOPLE_COUNTS_KEY);
        }
      } catch {
        sessionStorage.removeItem(CompaniesComponent.PEOPLE_COUNTS_KEY);
      }
    });
  }

  // Location formatting
  getLocation(address: any): string {
    if (!address) return 'Location not available';

    const parts = [
      address.city,
      address.region,
      address.country
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Location not available';
  }

  // Filter methods
  getCompanyLocationFilter(filters: any, types: any[]): any {
    if (!filters || !filters.filters) {
      return null;
    }

    // Find the company location filter by ID 10
    const locationFilter = filters.filters.find((filter: any) =>
      filter.filterType === 10 // Company Location ID
    );

    return locationFilter || null;
  }

  getSelectedFilters(formValue: any): any[] {
    const filtersArray = formValue?.filters || [];
    return filtersArray.filter((filter: any) => filter.filterType && filter.value);
  }

  getFilterLabel(filterId: number, types: any[]): string {
    const filterType = types.find(type => type.id === filterId);
    return filterType?.name || '';
  }

  // Format a filter for search
  private formatFilterForSearch(filter: any, filterType: any): any {
    // Handle range filter
    if (filterType.type === 'range' && Array.isArray(filter.value)) {
      // Filter out null values
      const rangeValues = filter.value.map(v => v === '' ? null : v);

      return {
        attribute: filterType.attribute,
        relation: filterType.relation,
        value: rangeValues
      };
    }

    // Handle tags type with special format
    if (filterType.type === 'tags' && Array.isArray(filter.value)) {
      return {
        attribute: filterType.attribute,
        relation: filterType.relation,
        value: {
          match: {
            operator: "or",
            operands: filter.value
          }
        }
      };
    }

    // Handle select type with special logic for single vs multiple selections
    if (filterType.type === 'select') {
      if (Array.isArray(filter.value) && filter.value.length > 1) {
        // Multiple selections - keep as array with 'in' relation
        return {
          attribute: filterType.attribute,
          relation: "in",
          value: filter.value
        };
      } else {
        // Single selection - use the value directly with 'equals' relation
        return {
          attribute: filterType.attribute,
          relation: "equals",
          value: Array.isArray(filter.value) ? filter.value[0] : filter.value
        };
      }
    }

    // Handle combobox-multiselect type - use the names directly
    if (filterType.type === 'combobox-multiselect' && Array.isArray(filter.value)) {
      // For multiselect, value already contains names
      if (filter.value.length > 1) {
        return {
          attribute: filterType.attribute,
          relation: "in",
          value: filter.value // Already contains names
        };
      } else if (filter.value.length === 1) {
        return {
          attribute: filterType.attribute,
          relation: "equals",
          value: filter.value[0]
        };
      }
      return null; // Empty value, skip
    }

    // Handle location filter
    if (filterType.type === 'location' && typeof filter.value === 'object') {
      const locationValue = filter.value;
      // Build location search object with only the location names
      const locationSearch: any = {};

      if (locationValue.country) {
        const country = this.countries.find(c => c.id === locationValue.country);
        if (country) {
          locationSearch.country = country.name;
        }
      }

      if (locationValue.region) {
        const countryRegions = this.regions[locationValue.country] || [];
        const region = countryRegions.find(r => r.id === locationValue.region);
        if (region) {
          locationSearch.region = region.name;
        }
      }

      if (locationValue.city) {
        const regionCities = this.cities[locationValue.region] || [];
        const city = regionCities.find(c => c.id === locationValue.city);
        if (city) {
          locationSearch.city = city.name;
        }
      }

      return {
        attribute: filterType.attribute,
        relation: "equals",
        value: locationSearch
      };
    }

    // Handle proximity filter (value is { address, radius, unit, strictness }).
    // Must match advanced-search's serialization: strictness is a sibling of
    // attribute/relation/value (NOT inside value) and is omitted when default (1).
    if (filterType.type === 'proximity' && filter.value && typeof filter.value === 'object') {
      const { address, radius, unit, strictness } = filter.value;
      const formatted: any = {
        attribute: filterType.attribute,
        relation: filterType.relation,
        value: {
          address: typeof address === 'string' ? address.trim() : address,
          radius: Number(radius),
          unit
        }
      };
      if (strictness === 2 || strictness === 3) {
        formatted.strictness = strictness;
      }
      return formatted;
    }

    // Handle input type - keep as string, not in array
    return {
      attribute: filterType.attribute,
      relation: filterType.relation,
      value: filter.value // Keep as string, not in array
    };
  }

  getFilterValue(filter: any, types: any[]): string {
    const filterType = types.find(type => type.id === filter.filterType);

    // Handle location filter (ID 10)
    if (filterType?.id === 10 && filter.value) {
      const locationParts = [];

      // Check for country, region, city data
      // First try to use the countryData, regionData, cityData objects if available
      if (filter.value.countryData) {
        locationParts.push(filter.value.countryData.name);
      } else if (filter.value.country) {
        // Fallback to using the IDs if data objects aren't available
        const countryId = filter.value.country;
        // You might need to fetch the country name based on ID
        // This is a placeholder, replace with actual implementation
        locationParts.push("Country");
      }

      if (filter.value.regionData) {
        locationParts.push(filter.value.regionData.name);
      } else if (filter.value.region) {
        // Fallback
        locationParts.push("Region");
      }

      if (filter.value.cityData) {
        locationParts.push(filter.value.cityData.name);
      } else if (filter.value.city) {
        // Fallback
        locationParts.push("City");
      }

      return locationParts.length > 0 ? locationParts.join(', ') : 'Any location';
    }

    // Handle proximity filter (value is { address, radius, unit, strictness }).
    // Show only the location and the radius — strictness/unit internals stay out of the chip.
    if (filterType?.type === 'proximity' && filter.value && typeof filter.value === 'object') {
      const { address, radius, unit } = filter.value;
      const parts = [];
      if (address) parts.push(address);
      if (radius !== null && radius !== undefined && radius !== '') {
        parts.push(`${radius} ${unit || 'km'}`);
      }
      return parts.length > 0 ? parts.join(' · ') : 'Any location';
    }

    // Handle select and combobox-multiselect filters
    if (filterType?.type === 'select' || filterType?.type === 'combobox-multiselect') {
      if (Array.isArray(filter.value)) {
        // Handle multiple selections
        return filter.value.join(', ');
      } else {
        // Handle single selection
        const option = filterType.options?.find(opt => opt.id === filter.value);
        return option?.name || filter.value?.toString() || '';
      }
    }

    // Handle range filter
    if (filterType?.type === 'range' && Array.isArray(filter.value)) {
      const [min, max] = filter.value;
      if (min !== null && max !== null) {
        return `${min} - ${max}`;
      } else if (min !== null) {
        return `≥ ${min}`;
      } else if (max !== null) {
        return `≤ ${max}`;
      }
    }

    // Handle tags filter
    if (filterType?.type === 'tags' && Array.isArray(filter.value)) {
      return filter.value.join(', ');
    }

    // Default case for string values
    return filter.value?.toString() || '';
  }

  // Person filter check
  isPersonFilter(filterId: number, types: any[]): boolean {
    // Check by ID (11 = person_titles, 12 = person_seniorities)
    if (filterId === 11 || filterId === 12) {
      return true;
    }

    // Check by applied attribute
    const filterType = types.find(type => type.id === filterId);
    if (filterType && filterType.applied === 'person') {
      return true;
    }

    // Check by attribute name
    if (filterType && (
      filterType.attribute === 'person_titles' ||
      filterType.attribute === 'person_seniorities'
    )) {
      return true;
    }

    return false;
  }

  // Modal methods
  openCompanyDetails(company: any): void {
    this.selectedCompany = company;
    this.isModalVisible = true;
  }

  closeModal(): void {
    this.isModalVisible = false;
    this.selectedCompany = null;
  }

  // Export modal methods
  openExportModal(): void {
    // Prepare the filters for export
    combineLatest([
      this.searchFilters$,
      this.filterTypes$
    ]).pipe(take(1)).subscribe(([searchFilters, filterTypes]) => {
      if (searchFilters && searchFilters.filters) {
        this.currentSearchFilters = this.prepareCompanyFilters(searchFilters, filterTypes);
      } else {
        this.currentSearchFilters = [];
      }
      this.isExportModalVisible = true;
    });
  }

  closeExportModal(): void {
    this.isExportModalVisible = false;
  }

  // "Proceed to Leads" methods
  openProceedModal(): void {
    combineLatest([
      this.searchFilters$,
      this.filterTypes$
    ]).pipe(take(1)).subscribe(([searchFilters, filterTypes]) => {
      this.proceedFilters = searchFilters && searchFilters.filters
        ? this.prepareCompanyFilters(searchFilters, filterTypes)
        : [];
      this.proceedSummaryLabel = this.buildSummaryLabel(searchFilters, filterTypes || []);
      this.isProceedModalVisible = true;
    });
  }

  closeProceedModal(): void {
    this.isProceedModalVisible = false;
  }

  /** Short human summary of the active company filters, e.g. "Software · Romania · 20–100 employees". */
  private buildSummaryLabel(formValue: any, types: any[]): string {
    const parts: string[] = [];
    for (const filter of this.getSelectedFilters(formValue)) {
      if (this.isPersonFilter(filter.filterType, types)) {
        continue;
      }
      const filterType = types.find(type => type.id === filter.filterType);
      let value = (this.getFilterValue(filter, types) || '').trim();
      if (!value) {
        continue;
      }
      if (filterType?.type === 'range') {
        value = `${value.replace(' - ', '–')} ${(filterType.name || '').toLowerCase()}`.trim();
      }
      parts.push(value);
    }
    const label = parts.join(' · ') || 'Advanced Prospecting search';
    return label.length > 120 ? label.slice(0, 119).trimEnd() + '…' : label;
  }

  loadProspectingLists(): void {
    this.subscription.add(
      this.prospectingService.getLists().subscribe({
        next: (lists) => {
          this.prospectingLists = (lists || []).filter(list => !list.saved_icp_id);
        },
        error: () => {
          this.prospectingLists = [];
        }
      })
    );
  }

  toggleProspectingLists(): void {
    this.isProspectingListsOpen = !this.isProspectingListsOpen;
  }

  openProspectingList(list: ProspectingListSummary): void {
    this.isProspectingListsOpen = false;
    this.router.navigate(['/leads'], { queryParams: { list: list.list_id } });
  }

  // Formatting helpers
  formatEmployeeCount(count: number | string): string {
    if (!count) return '';

    const num = typeof count === 'string' ? parseInt(count) : count;
    if (isNaN(num)) return '';

    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  formatRevenue(revenue: number | string): string {
    if (!revenue) return '';

    const num = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
    if (isNaN(num)) return '';

    if (num >= 1000000000) {
      const billions = Math.round(num / 1000000000);
      return billions + 'B';
    } else if (num >= 1000000) {
      const millions = Math.round(num / 1000000);
      return millions + 'M';
    } else if (num >= 1000) {
      const thousands = Math.round(num / 1000);
      return thousands + 'K';
    }
    return Math.round(num).toString();
  }
}
